import { useEffect, useState } from 'react';
import { useWidgetAuth } from '../context/AuthContext';
import {
  loadUserSettingsPreferences,
  saveUserSettingsPreferences,
} from '../utils/userPreferences';

export type FontSize = 'small' | 'medium' | 'large';

export interface FontSizeOption {
  id: FontSize;
  displayName: string;
  description: string;
  multiplier: number;
}

export const FONT_SIZE_OPTIONS: FontSizeOption[] = [
  {
    id: 'small',
    displayName: 'Small',
    description: 'Smaller text',
    multiplier: 1.0,
  },
  {
    id: 'medium',
    displayName: 'Medium',
    description: 'Default text size',
    multiplier: 1.33,
  },
  {
    id: 'large',
    displayName: 'Large',
    description: 'Larger text',
    multiplier: 1.66,
  },
];

export const useFontSizeSettings = () => {
  const { user } = useWidgetAuth();
  const userId = user?.username || user?.email;

  const [selectedFontSize, setSelectedFontSize] = useState<FontSize>('medium');

  useEffect(() => {
    if (userId) {
      const preferences = loadUserSettingsPreferences(userId);
      setSelectedFontSize(preferences.fontSizePreference || 'medium');
    }
  }, [userId]);

  const handleFontSizeSelect = (fontSize: FontSize) => {
    setSelectedFontSize(fontSize);
    if (userId) {
      saveUserSettingsPreferences(userId, {
        fontSizePreference: fontSize,
      });
    }
  };

  const getFontSizeMultiplier = (): number => {
    return (
      FONT_SIZE_OPTIONS.find((option) => option.id === selectedFontSize)
        ?.multiplier || 1.33
    );
  };

  return {
    selectedFontSize,
    handleFontSizeSelect,
    getFontSizeMultiplier,
  };
};
